/** `/lotteries`: list with status badges, filters and Appendix A lifecycle actions per row. */
import { useQuery } from '@tanstack/react-query';
import { LotteryMechanic, LotteryStatus, type LotteryListRowDto } from '@windrop/contracts';
import { Button, useIntl } from '@windrop/ui';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Can } from '../../auth/guards';
import { DataTable, type Column } from '../../components/DataTable';
import { EmptyState, ErrorState } from '../../components/EmptyState';
import { Chip, Chips, ClearFilters, FilterBar, FilterField, FilterSelect } from '../../components/FilterBar';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge, humanizeStatus } from '../../components/StatusBadge';
import { lotteriesApi } from '../../lib/api/admin';
import { isApiError } from '../../lib/api/client';
import { LifecycleActions } from './lifecycle';

/** Quick views over the Appendix A lifecycle: what an operator looks for first. */
const QUICK_VIEWS: { id: string; label: string; statuses: LotteryStatus[] }[] = [
  { id: 'all', label: 'All', statuses: [] },
  { id: 'live', label: 'Live', statuses: ['LIVE', 'PAUSED'] },
  { id: 'upcoming', label: 'Scheduled', statuses: ['SCHEDULED', 'APPROVED'] },
  { id: 'drafts', label: 'Drafts & review', statuses: ['DRAFT', 'REVIEW'] },
  { id: 'finished', label: 'Closed & drawn', statuses: ['CLOSED', 'DRAWN', 'SETTLED', 'CANCELLED'] },
];

export function LotteriesListPage() {
  const intl = useIntl();
  const [status, setStatus] = useState<string>('');
  const [mechanic, setMechanic] = useState<string>('');
  const [view, setView] = useState('all');
  const query = useQuery({
    queryKey: ['lotteries', { status, mechanic }],
    queryFn: () =>
      lotteriesApi.list({
        status: status ? (status as LotteryStatus) : undefined,
        mechanic: mechanic ? (mechanic as LotteryMechanic) : undefined,
      }),
  });
  const quick = QUICK_VIEWS.find((v) => v.id === view) ?? QUICK_VIEWS[0]!;
  const rows = useMemo(() => {
    const items = query.data?.items ?? [];
    return quick.statuses.length ? items.filter((r) => quick.statuses.includes(r.status)) : items;
  }, [query.data, quick]);
  const countFor = (v: (typeof QUICK_VIEWS)[number]) => {
    const items = query.data?.items;
    if (!items) return null;
    return v.statuses.length ? items.filter((r) => v.statuses.includes(r.status)).length : items.length;
  };

  const columns = useMemo<Column<LotteryListRowDto>[]>(
    () => [
      {
        id: 'title',
        header: 'Lottery',
        sortValue: (r) => r.title.toLowerCase(),
        filterValue: (r) => `${r.title} ${r.slug}`,
        cell: (r) => (
          <>
            <Link to={`/lotteries/${r.lotteryId}`}>
              <strong>{r.title}</strong>
            </Link>
            <br />
            <code className="wd-muted">{r.slug}</code>
          </>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        sortValue: (r) => r.status,
        cell: (r) => <StatusBadge kind="LotteryStatus" value={r.status} />,
      },
      {
        id: 'mechanic',
        header: 'Mechanic',
        sortValue: (r) => r.mechanic,
        cell: (r) => humanizeStatus(r.mechanic),
      },
      {
        id: 'opensAt',
        header: 'Opens',
        sortValue: (r) => r.opensAt,
        cell: (r) => intl.formatDateTime(r.opensAt),
      },
      {
        id: 'closesAt',
        header: 'Closes',
        sortValue: (r) => r.closesAt,
        cell: (r) => intl.formatDateTime(r.closesAt),
      },
      {
        id: 'version',
        header: 'Version',
        sortValue: (r) => r.configVersion,
        align: 'end',
        cell: (r) => (
          <span className="wd-tabular">
            v{r.configVersion}
            {r.publishedConfigVersion !== null && r.publishedConfigVersion !== r.configVersion
              ? ` (published v${r.publishedConfigVersion})`
              : ''}
          </span>
        ),
      },
      {
        id: 'entries',
        header: 'Entries',
        cell: (r) => (r.hasEntries ? 'Yes — rules locked' : 'None yet'),
        defaultHidden: true,
      },
      {
        id: 'updatedAt',
        header: 'Updated',
        sortValue: (r) => r.updatedAt,
        cell: (r) => intl.formatRelative(r.updatedAt),
        defaultHidden: true,
      },
    ],
    [intl],
  );

  const filtersActive = Boolean(status || mechanic || view !== 'all');

  return (
    <>
      <PageHeader
        title="Lotteries"
        description="Every lottery in this tenant with its Appendix A state. Actions respect your role."
        actions={
          <Can permission="lottery.create">
            <Button href="/lotteries/new">Create lottery</Button>
          </Can>
        }
      >
        <Chips label="Quick views">
          {QUICK_VIEWS.map((v) => (
            <Chip key={v.id} pressed={view === v.id} onClick={() => setView(v.id)} count={countFor(v)}>
              {v.label}
            </Chip>
          ))}
        </Chips>
      </PageHeader>
      <FilterBar
        end={
          <ClearFilters
            active={filtersActive}
            onClick={() => {
              setStatus('');
              setMechanic('');
              setView('all');
            }}
          />
        }
      >
        <FilterField label="Status" htmlFor="lotteries-status">
          <FilterSelect
            id="lotteries-status"
            value={status}
            onChange={setStatus}
            options={[
              { value: '', label: 'All statuses' },
              ...LotteryStatus.options.map((s) => ({ value: s, label: humanizeStatus(s) })),
            ]}
          />
        </FilterField>
        <FilterField label="Mechanic" htmlFor="lotteries-mechanic">
          <FilterSelect
            id="lotteries-mechanic"
            value={mechanic}
            onChange={setMechanic}
            options={[
              { value: '', label: 'All mechanics' },
              ...LotteryMechanic.options.map((m) => ({ value: m, label: humanizeStatus(m) })),
            ]}
          />
        </FilterField>
      </FilterBar>
      {query.isError ? (
        <ErrorState
          title="Could not load lotteries"
          body="Retry, or check the API status if this persists."
          reference={isApiError(query.error) ? query.error.supportReference : undefined}
          action={<Button onClick={() => query.refetch()}>Retry</Button>}
        />
      ) : (
        <DataTable
          caption="Lotteries"
          columns={columns}
          rows={rows}
          rowKey={(r) => r.lotteryId}
          loading={query.isPending}
          initialSort={{ columnId: 'updatedAt', direction: 'desc' }}
          filterPlaceholder="Filter by title or slug"
          emptyState={
            filtersActive ? (
              <EmptyState
                title="No lotteries match these filters"
                body="Widen the quick view or clear the status and mechanic filters."
                action={
                  <Button
                    onClick={() => {
                      setStatus('');
                      setMechanic('');
                      setView('all');
                    }}
                  >
                    Clear filters
                  </Button>
                }
              />
            ) : (
              <EmptyState
                title="No lotteries yet"
                body="Create the first one with the wizard; templates and autosave get you to publish in about ten minutes."
                action={
                  <Can permission="lottery.create" fallback={<Button href="/">Back to dashboard</Button>}>
                    <Button href="/lotteries/new">Create lottery</Button>
                  </Can>
                }
              />
            )
          }
          rowActions={(r) => (
            <div className="wd-btn-group">
              {r.status === 'DRAFT' || r.status === 'REVIEW' || r.status === 'APPROVED' ? (
                <Can permission="lottery.update">
                  <Link to={`/lotteries/${r.lotteryId}/edit`} className="wd-btn" data-variant="quiet">
                    Edit
                  </Link>
                </Can>
              ) : null}
              <LifecycleActions
                lotteryId={r.lotteryId}
                slug={r.slug}
                status={r.status}
                affected={r.hasEntries ? undefined : { affected: 0, detail: 'no entries yet' }}
              />
            </div>
          )}
        />
      )}
    </>
  );
}
